import React, { useEffect, useState } from "react";
import { api } from "../../api/client";
import ConfirmModal from "../../components/ConfirmModal";

const STATUT_INFO = {
  en_attente: { label: "En attente",  color: "#f39c12" },
  validee:    { label: "Validée",     color: "#1d4f91" },
  retiree:    { label: "Empruntée",   color: "#27ae60" },
  rendue:     { label: "Rendue",      color: "#888"    },
  refusee:    { label: "Refusée",     color: "#e74c3c" },
  annulee:    { label: "Annulée",     color: "#888"    },
};

function fmtFR(iso) {
  if (!iso) return "";
  return new Date(iso + (iso.includes("T") ? "" : "T00:00:00"))
    .toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

function joursRestants(dateRetour) {
  if (!dateRetour) return null;
  const retour = new Date(dateRetour + "T00:00:00");
  const now = new Date(); now.setHours(0,0,0,0);
  return Math.round((retour - now) / (1000 * 60 * 60 * 24));
}

export default function ReservationsAdmin() {
  const [tab, setTab] = useState("demandes");
  const [reservations, setReservations] = useState([]);
  const [compteurs, setCompteurs] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionResa, setActionResa] = useState(null); // {resa, action: valider|refuser|retirer|rendre}

  const reload = () => {
    setLoading(true);
    Promise.all([
      api.get("/bibliotheque/admin/reservations"),
      api.get("/bibliotheque/admin/reservations/compteurs"),
    ]).then(([r1, r2]) => {
      setReservations(r1.items || []);
      setCompteurs(r2 || {});
    }).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const demandes = reservations.filter((r) => r.statut === "en_attente");
  const enCours = reservations.filter((r) => ["validee","retiree"].includes(r.statut));
  const historique = reservations.filter((r) => ["rendue","refusee","annulee"].includes(r.statut));

  return (
    <div>
      <div className="biblio-admin-tabs" style={{ marginTop: "1rem" }}>
        <button className={`biblio-admin-tab ${tab === "demandes" ? "active" : ""}`}
                onClick={() => setTab("demandes")}>
          📝 Demandes
          {demandes.length > 0 && (
            <span className="resa-badge">{demandes.length}</span>
          )}
        </button>
        <button className={`biblio-admin-tab ${tab === "encours" ? "active" : ""}`}
                onClick={() => setTab("encours")}>
          📚 En cours
          {compteurs.retards > 0 && (
            <span className="resa-badge danger">{compteurs.retards} retard{compteurs.retards > 1 ? "s" : ""}</span>
          )}
        </button>
        <button className={`biblio-admin-tab ${tab === "historique" ? "active" : ""}`}
                onClick={() => setTab("historique")}>
          🗂 Historique ({historique.length})
        </button>
      </div>

      {loading && <p className="muted">Chargement…</p>}

      {!loading && tab === "demandes" && (
        <DemandesPanel items={demandes} onAction={setActionResa} />
      )}
      {!loading && tab === "encours" && (
        <EnCoursPanel items={enCours} onAction={setActionResa} />
      )}
      {!loading && tab === "historique" && (
        <HistoriquePanel items={historique} />
      )}

      {actionResa && (
        <ActionModal resa={actionResa.resa} action={actionResa.action}
                     onClose={() => setActionResa(null)}
                     onDone={() => { setActionResa(null); reload(); }} />
      )}
    </div>
  );
}

function DemandesPanel({ items, onAction }) {
  if (items.length === 0) {
    return (
      <p className="muted" style={{ textAlign: "center", padding: "2rem" }}>
        Aucune demande en attente.
      </p>
    );
  }
  return (
    <div className="resa-admin-list">
      {items.map((r) => (
        <div key={r.id} className="resa-admin-card">
          <div className="resa-admin-card-head">
            <div>
              <strong>{r.livre_titre}</strong>
              {r.livre_auteur && <span className="muted"> — {r.livre_auteur}</span>}
            </div>
            <span className="resa-statut-pill" style={{ background: STATUT_INFO.en_attente.color }}>
              En attente
            </span>
          </div>
          <div className="resa-admin-info">
            <div><strong>Lecteur :</strong> {r.user_nom}</div>
            <div><strong>Email :</strong> <a href={`mailto:${r.user_email}`}>{r.user_email}</a></div>
            <div><strong>Demande :</strong> {fmtFR(r.date_demande.split("T")[0])}</div>
          </div>
          {r.commentaire_libraire && (
            <div className="resa-comment">
              <strong>Message du lecteur :</strong> {r.commentaire_libraire}
            </div>
          )}
          <div className="resa-admin-actions">
            <button className="btn-primary"
                    onClick={() => onAction({ resa: r, action: "valider" })}>
              ✓ Valider
            </button>
            <button className="secondary"
                    onClick={() => onAction({ resa: r, action: "refuser" })}>
              ✕ Refuser
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function EnCoursPanel({ items, onAction }) {
  if (items.length === 0) {
    return (
      <p className="muted" style={{ textAlign: "center", padding: "2rem" }}>
        Aucune réservation en cours.
      </p>
    );
  }
  // Trier : retards d'abord, puis validees, puis retirees
  const tri = (a, b) => {
    const ja = joursRestants(a.date_retour_prevue);
    const jb = joursRestants(b.date_retour_prevue);
    if (a.statut === "validee" && b.statut !== "validee") return -1;
    if (b.statut === "validee" && a.statut !== "validee") return 1;
    if (ja === null) return 1;
    if (jb === null) return -1;
    return ja - jb;
  };
  return (
    <div className="resa-admin-list">
      {[...items].sort(tri).map((r) => {
        const info = STATUT_INFO[r.statut];
        const jours = joursRestants(r.date_retour_prevue);
        const enRetard = jours !== null && jours < 0;
        return (
          <div key={r.id} className={`resa-admin-card ${enRetard ? "en-retard" : ""}`}>
            <div className="resa-admin-card-head">
              <div>
                <strong>{r.livre_titre}</strong>
                {r.livre_auteur && <span className="muted"> — {r.livre_auteur}</span>}
              </div>
              <span className="resa-statut-pill" style={{ background: info.color }}>
                {info.label}
              </span>
            </div>
            <div className="resa-admin-info">
              <div><strong>Lecteur :</strong> {r.user_nom}</div>
              <div><strong>Email :</strong> <a href={`mailto:${r.user_email}`}>{r.user_email}</a></div>
              {r.statut === "validee" && (
                <div><strong>Validée le :</strong> {fmtFR(r.date_demande.split("T")[0])}</div>
              )}
              {r.statut === "retiree" && (
                <>
                  <div><strong>Retiré le :</strong> {fmtFR(r.date_retrait)}</div>
                  <div>
                    <strong>Retour prévu :</strong> {fmtFR(r.date_retour_prevue)}
                    {enRetard && (
                      <span className="resa-retard-badge"> En retard de {-jours} j</span>
                    )}
                    {!enRetard && jours !== null && jours <= 3 && (
                      <span className="resa-bientot-badge">
                        {" "}{jours === 0 ? "À rendre aujourd'hui" : `Dans ${jours} j`}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="resa-admin-actions">
              {r.statut === "validee" && (
                <>
                  <button className="btn-primary"
                          onClick={() => onAction({ resa: r, action: "retirer" })}>
                    📚 Marquer retiré
                  </button>
                  <button className="secondary"
                          onClick={() => onAction({ resa: r, action: "refuser" })}>
                    ✕ Annuler
                  </button>
                </>
              )}
              {r.statut === "retiree" && (
                <button className="btn-primary"
                        onClick={() => onAction({ resa: r, action: "rendre" })}>
                  ↩ Marquer rendu
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HistoriquePanel({ items }) {
  if (items.length === 0) {
    return (
      <p className="muted" style={{ textAlign: "center", padding: "2rem" }}>
        Aucune réservation historique.
      </p>
    );
  }
  return (
    <table className="biblio-admin-table">
      <thead>
        <tr>
          <th>Livre</th>
          <th>Lecteur</th>
          <th>Statut</th>
          <th>Demandé le</th>
          <th>Rendu le</th>
        </tr>
      </thead>
      <tbody>
        {items.map((r) => {
          const info = STATUT_INFO[r.statut];
          return (
            <tr key={r.id}>
              <td>
                <strong>{r.livre_titre}</strong>
                {r.livre_auteur && <div className="muted" style={{ fontSize: "0.85em" }}>{r.livre_auteur}</div>}
              </td>
              <td>
                {r.user_nom}
                <div className="muted" style={{ fontSize: "0.82em" }}>{r.user_email}</div>
              </td>
              <td>
                <span style={{ color: info.color, fontWeight: 500 }}>● {info.label}</span>
              </td>
              <td>{fmtFR(r.date_demande.split("T")[0])}</td>
              <td>{r.date_retour_effective ? fmtFR(r.date_retour_effective) : "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ===================== MODAL D'ACTION =====================

function ActionModal({ resa, action, onClose, onDone }) {
  const [commentaire, setCommentaire] = useState("");
  const [duree, setDuree] = useState(21);  // valeur par défaut
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Charger la durée par défaut depuis les paramètres
  useEffect(() => {
    if (action === "retirer") {
      api.get("/parametres").then((p) => {
        const def = parseInt(p.bibliotheque_duree_pret_jours_defaut || "21", 10);
        if (def > 0) setDuree(def);
      }).catch(() => {});
    }
  }, [action]);

  const titre = {
    valider: "Valider la demande",
    refuser: "Refuser ou annuler",
    retirer: "Marquer comme retiré",
    rendre:  "Marquer comme rendu",
  }[action];

  const submitLabel = {
    valider: "Valider",
    refuser: "Refuser",
    retirer: "Confirmer le retrait",
    rendre:  "Confirmer le retour",
  }[action];

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    try {
      const url = `/bibliotheque/admin/reservations/${resa.id}/${action}`;
      const payload = { commentaire };
      if (action === "retirer") payload.duree_jours = parseInt(duree, 10);
      await api.patch(url, payload);
      onDone();
    } catch (e) {
      setError(e.message || "Erreur");
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3>{titre}</h3>
          <button className="modal-close" onClick={() => !saving && onClose()}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}
          <p style={{ marginTop: 0 }}>
            <strong>{resa.livre_titre}</strong>
            {resa.livre_auteur && <span className="muted"> — {resa.livre_auteur}</span>}
          </p>
          <p className="muted" style={{ fontSize: "0.9em" }}>
            Lecteur : {resa.user_nom} ({resa.user_email})
          </p>

          {action === "retirer" && (
            <label style={{ display: "block", marginTop: "1rem" }}>
              <strong>Durée du prêt (en jours) :</strong>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem",
                            marginTop: "0.4rem" }}>
                <input type="number" min="1" max="60" value={duree}
                       onChange={(e) => setDuree(e.target.value)}
                       style={{ width: 100, padding: "0.5rem",
                                border: "1px solid rgba(0,0,0,0.2)",
                                borderRadius: 5 }} />
                <span className="muted">jours (entre 1 et 60 — soit 2 mois max)</span>
              </div>
              <div className="muted" style={{ fontSize: "0.85em", marginTop: "0.4rem" }}>
                Retour prévu :{" "}
                <strong>
                  {new Date(Date.now() + duree * 86400000)
                    .toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                </strong>
              </div>
            </label>
          )}

          <label style={{ display: "block", marginTop: "1rem" }}>
            Commentaire (facultatif) :
            <textarea value={commentaire}
                      onChange={(e) => setCommentaire(e.target.value)}
                      rows="3"
                      placeholder={action === "refuser" ? "Motif du refus..." :
                                   "Note interne ou message au lecteur..."}
                      style={{ width: "100%", marginTop: "0.4rem",
                               padding: "0.5rem", border: "1px solid rgba(0,0,0,0.18)",
                               borderRadius: 5, fontFamily: "inherit" }} />
          </label>
        </div>
        <div className="modal-footer">
          <button className="secondary" onClick={onClose} disabled={saving}>
            Annuler
          </button>
          <button className={action === "refuser" ? "danger" : "btn-primary"}
                  onClick={handleSubmit} disabled={saving}>
            {saving ? "..." : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
